import { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Helmet } from 'react-helmet-async';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Calculator, 
  ChevronRight,
  IndianRupee,
  Package,
  Truck,
  BarChart3,
  TrendingUp,
  Eye,
  EyeOff,
  Info,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

// ============================================================
// FEE SCHEDULES - FROM EXCEL (Effective March 16, 2026)
// ============================================================

// CLOSING FEES by Mode and Price Slab
const CLOSING_FEES = {
  'Easy Ship': { 0: 1, 300: 22, 500: 45, 1000: 76 },
  'FBA': { 0: 26, 300: 22, 500: 27, 1000: 52 },
  'Seller Flex': { 0: 6, 300: 12, 500: 35, 1000: 66 },
  'Self Ship': { 0: 20, 300: 26, 500: 51, 1000: 101 },
};

// FBA WEIGHT HANDLING FEES
const FBA_WEIGHT_FEES = {
  'Premium': {
    'Regional': { base_0_5: 37, base_0_5_1: 52, base_1_2: 76, per_kg_2_5: 24, per_kg_above_5: 13 },
    'National': { base_0_5: 63, base_0_5_1: 83, base_1_2: 120, per_kg_2_5: 34, per_kg_above_5: 18 },
  },
  'Standard': {
    'Regional': { base_0_5: 39, base_0_5_1: 54, base_1_2: 78, per_kg_2_5: 24, per_kg_above_5: 13 },
    'National': { base_0_5: 65, base_0_5_1: 85, base_1_2: 122, per_kg_2_5: 34, per_kg_above_5: 18 },
  },
  'Basic': {
    'Regional': { base_0_5: 42, base_0_5_1: 58, base_1_2: 82, per_kg_2_5: 24, per_kg_above_5: 13 },
    'National': { base_0_5: 69, base_0_5_1: 89, base_1_2: 126, per_kg_2_5: 34, per_kg_above_5: 18 },
  },
};

// SELLER FLEX WEIGHT HANDLING FEES
const SELLER_FLEX_WEIGHT_FEES = {
  'Premium': { base_0_5: 49, base_0_5_1: 69, base_1_2: 106, per_kg_2_5: 34, per_kg_above_5: 18 },
  'Standard': { base_0_5: 51, base_0_5_1: 71, base_1_2: 108, per_kg_2_5: 34, per_kg_above_5: 18 },
  'Basic': { base_0_5: 55, base_0_5_1: 75, base_1_2: 112, per_kg_2_5: 34, per_kg_above_5: 18 },
};

// EASY SHIP WEIGHT HANDLING FEES
const EASY_SHIP_WEIGHT_FEES = {
  'Premium': { base_0_5: 53, base_0_5_1: 73, base_1_2: 110, per_kg_2_5: 34, per_kg_above_5: 18 },
  'Standard': { base_0_5: 55, base_0_5_1: 75, base_1_2: 112, per_kg_2_5: 34, per_kg_above_5: 18 },
  'Basic': { base_0_5: 59, base_0_5_1: 79, base_1_2: 116, per_kg_2_5: 34, per_kg_above_5: 18 },
};

// OTHER FEES
const FBA_PICK_PACK_FEE = 17;
const SELLER_FLEX_TECH_FEE = 14;
const GST_RATE = 0.18;

// REFERRAL FEE SCHEDULE - Category-based with tier pricing
// Format: [T1_Max, T1_Rate%, T2_Max, T2_Rate%, T3_Max, T3_Rate%, T4_Max, T4_Rate%]
const REFERRAL_FEE_SCHEDULE = {
  'Helmets & Riding Gloves': [1000, 0, 9999999, 8.5],
  'Tyres & Rims': [1000, 0, 9999999, 3],
  'Automotive Vehicles (2W/4W/EV)': [1000, 0, 50000, 5, 9999999, 2],
  'Car & Bike Parts, Brakes, Styling': [1000, 0, 9999999, 16],
  'Cleaning Kits, Car/Bike Lighting, Paints': [1000, 0, 9999999, 13],
  'Automotive Accessories (Floor Mats, Covers)': [1000, 0, 9999999, 14],
  'Vehicle Tools and Appliances': [1000, 0, 9999999, 8.5],
  'Oils, Lubricants': [1000, 0, 9999999, 11.5],
  'Batteries and Air Fresheners': [1000, 0, 9999999, 8.5],
  'Car Electronics Devices': [300, 0, 500, 7.5, 1000, 9.5, 9999999, 12],
  'Car Cradles, Lens Kits & Tablet Cases': [300, 0, 1000, 5, 9999999, 28.5],
  'Car Electronics Accessories': [300, 0, 1000, 5, 9999999, 15],
  'Automotive - Other Products': [1000, 0, 9999999, 16],
  'Toys – Drones': [1000, 0, 9999999, 30],
  'Toys – Party Supplies, Balloons': [1000, 0, 9999999, 12.5],
  'Toys – Games & Puzzles, Board Games': [1000, 0, 9999999, 12.5],
  'Toys – Infant & Pre-school Toys': [1000, 0, 9999999, 11.5],
  'Toys – Outdoor, Activity & Sports': [1000, 0, 9999999, 10.5],
  'Toys – Plush, Action Figures & Dolls': [1000, 0, 9999999, 10.5],
  'Toys – RC & Non-RC Vehicles': [1000, 0, 9999999, 12.5],
  'Toys – STEM, Art & Craft': [1000, 0, 9999999, 11.5],
  'Toys – Other Products': [1000, 0, 9999999, 11.5],
  'Baby Hardlines (Swings, Carriers, Walkers)': [1000, 0, 9999999, 6.5],
  'Baby Strollers': [1000, 0, 9999999, 9],
  'Baby & Kids – Furniture and Home Decor': [1000, 0, 9999999, 8.5],
  'Baby – Walker': [1000, 0, 9999999, 5],
  'Baby – Diapers': [1000, 0, 9999999, 9.5],
  'Baby – Other Products': [1000, 0, 9999999, 7.5],
  'Breast Pumps': [1000, 0, 9999999, 7.5],
  'Diaper Bags': [1000, 0, 9999999, 7.5],
  'Books': [250, 0, 500, 2, 1000, 4, 9999999, 13.5],
  'School Textbook Bundles': [250, 2, 1000, 3, 1500, 4, 9999999, 4.5],
  'Video Games – Consoles': [500, 7, 1000, 5, 9999999, 9],
  'Video Games – Accessories': [300, 0, 1000, 5, 9999999, 13.5],
  'Video Games – Online Game Services': [1000, 0, 2000, 2, 9999999, 3],
  'Video Games – Other Products': [300, 0, 1000, 5, 9999999, 12],
  'Movies': [500, 6.5, 1000, 0, 9999999, 6.5],
  'Music': [500, 6.5, 1000, 0, 9999999, 6.5],
  'Musical Instruments – DJ/VJ/Recording/PA': [300, 6, 500, 4.5, 1000, 0, 9999999, 11],
  'Musical Instruments – Microphones': [300, 9.5, 1000, 0, 9999999, 11.5],
  'Musical Instruments – Keyboards': [500, 8, 1000, 0, 9999999, 8],
  'Musical Instruments – Guitars': [500, 10, 1000, 0, 9999999, 10],
  'Musical Instruments – Other Products': [1000, 0, 9999999, 11],
  'Entertainment Collectibles': [300, 13, 9999999, 17],
  'Masks': [1000, 0, 9999999, 8],
  '3D Printers': [1000, 0, 9999999, 12],
  'Business & Industrial – Scientific Supplies': [1000, 0, 15000, 11.5, 9999999, 7],
  'Business & Industrial – Commercial, Food Handling': [1000, 0, 9999999, 5.5],
  'Business & Industrial – Electrical Testing, Thermal Printers': [1000, 0, 9999999, 6],
  'Power & Hand Tools and Water Dispensers': [1000, 0, 9999999, 10],
  'Stethoscopes': [1000, 0, 9999999, 10.5],
  'Packing Materials': [1000, 0, 9999999, 5],
  'Office Products – Office Supplies': [1000, 0, 9999999, 13],
  'Office Products – Electronic Devices': [1000, 0, 9999999, 11.5],
  'Office Products – Arts and Crafts': [1000, 0, 9999999, 5],
  'Office Products – Writing Instruments': [1000, 0, 9999999, 14],
  'Business & Industrial – Other Products': [1000, 0, 15000, 8, 9999999, 5],
  'Office – Other Products': [1000, 0, 9999999, 11.5],
  'Apparel – Accessories': [1000, 0, 9999999, 19],
  'Apparel – Sweat Shirts & Jackets': [1000, 0, 9999999, 18],
  'Apparel – Shorts': [1000, 0, 9999999, 24],
  'Apparel – Baby': [1000, 0, 9999999, 7],
  'Apparel – Ethnic Wear': [1000, 0, 9999999, 16.5],
  'Apparel – Other Innerwear': [1000, 0, 9999999, 18.5],
  'Apparel – Sleepwear': [1000, 0, 9999999, 19],
  'Apparel – Sarees & Dress Materials': [1000, 0, 9999999, 15],
  "Apparel – Men's T-Shirts": [1000, 0, 9999999, 23],
  "Apparel – Women's Innerwear & Lingerie": [1000, 0, 9999999, 18],
  'Apparel – Dress': [1000, 0, 9999999, 19],
  'Apparel – Shirts': [1000, 0, 9999999, 21],
  'Apparel – Socks & Stockings': [1000, 0, 9999999, 19],
  'Apparel – Thermals': [1000, 0, 9999999, 19],
  'Apparel – Other Products': [1000, 0, 9999999, 19],
  'Pants – Trousers, Jeans, Trackpants, Leggings': [1000, 0, 9999999, 19],
  'Backpacks': [1000, 0, 9999999, 14.5],
  'Eyewear – Sunglasses, Frames': [1000, 0, 9999999, 18.5],
  'Fashion Jewellery': [1000, 0, 9999999, 22.5],
  'Fine Jewellery – Gold Coins': [1000, 0, 9999999, 6],
  'Fine Jewellery – Studded': [1000, 0, 9999999, 13],
  'Fine Jewellery – Unstudded & Solitaire': [1000, 0, 9999999, 5],
  'Silver Jewellery': [1000, 0, 9999999, 13],
  'Silver Coins & Bars': [1000, 0, 9999999, 6],
  'Handbags': [1000, 0, 9999999, 12],
  'Luggage – Suitcase & Trolleys': [1000, 0, 9999999, 5.5],
  'Luggage – Travel Accessories': [1000, 0, 9999999, 12],
  'Luggage – Other Products': [1000, 0, 9999999, 8],
  'Flip Flops, Fashion Sandals & Slippers': [1000, 0, 9999999, 15],
  'Kids Shoes': [1000, 0, 9999999, 16],
  'Shoes': [1000, 0, 9999999, 8],
  'Wallets': [1000, 0, 9999999, 14],
  'Watches': [1000, 0, 9999999, 15],
  'Camera Lenses': [1000, 7, 9999999, 10],
  'Camera & Camcorder': [1000, 5, 19000, 7, 49000, 9, 9999999, 7],
  'Camera Accessories': [300, 0, 1000, 5, 9999999, 13.5],
  'Cases, Covers, Skins & Screen Guards': [1000, 0, 9999999, 25],
  'Accessories – Electronics, PC & Wireless': [300, 0, 1000, 5, 9999999, 17],
  'Electronic Devices (excl. TV/Camera/GPS/Speakers)': [1000, 9, 9999999, 11],
  'GPS Devices': [300, 13.5, 500, 12.5, 9999999, 13.5],
  'Hard Disks': [1000, 9.5, 9999999, 12.5],
  'Headsets, Headphones & Earphones': [1000, 0, 9999999, 18],
  'Keyboards & Mouse': [300, 0, 1000, 5, 9999999, 17],
  'Kindle Accessories': [9999999, 25],
  'Laptop & Camera Battery': [300, 14, 500, 12.5, 1000, 14, 9999999, 15.5],
  'Laptop Bags & Sleeves': [300, 0, 1000, 5, 9999999, 14.5],
  'Laptops': [9999999, 6],
  'Desktops': [9999999, 8],
  'Monitors': [1000, 6.5, 9999999, 8],
  'Mobile Phones': [9999999, 5],
  'Tablets (incl. Graphic Tablets)': [300, 0, 12000, 6, 9999999, 10],
  'Scanners & Printers': [1000, 9, 9999999, 10.5],
  'Memory Cards': [500, 16, 1000, 5, 9999999, 16],
  'Speakers': [500, 11, 1000, 11.5, 9999999, 14],
  'Power Banks & Chargers': [1000, 0, 9999999, 20.5],
  'Smart Watches & Accessories': [300, 0, 1000, 5, 9999999, 17],
  'PC Components – RAM & Motherboards': [300, 0, 9999999, 5.5],
  'Software Products': [1000, 0, 2000, 7.5, 9999999, 9.5],
  'Cables & Adapters': [1000, 0, 9999999, 20],
  'Television': [9999999, 6],
  'Modems & Networking Devices': [9999999, 14],
  'USB Flash Drives / Pen Drives': [9999999, 16],
  'Landline Phones': [9999999, 7],
  'Projectors, Home Theatre, Binoculars & Telescopes': [9999999, 6],
  'Grocery – Herbs & Spices': [1000, 0, 9999999, 8],
  'Grocery – Dried Fruits & Nuts': [1000, 0, 9999999, 9],
  'Grocery – Hampers & Gifting': [1000, 0, 9999999, 11.5],
  'Grocery & Gourmet – Oils': [1000, 0, 9999999, 5],
  'Grocery & Gourmet – Beverages': [1000, 0, 9999999, 10],
  'Grocery & Gourmet – Other Products': [1000, 0, 9999999, 9],
  'Pet Foods': [300, 0, 1000, 6.5, 9999999, 9.5],
  'Pet – Aquatics Supplies': [1000, 0, 9999999, 14],
  'Pet Accessories (Apparel, Collar, Leash)': [1000, 0, 9999999, 12.5],
  'Pet Comforters (Beds, Feeders, Cages, Crates)': [1000, 0, 9999999, 12.5],
  'Pet Essentials (Health, Grooming, Dental)': [1000, 0, 9999999, 14],
  'Pet – Other Products': [1000, 0, 9999999, 12],
  'Beauty – Fragrance': [500, 0, 1000, 14, 9999999, 10],
  'Beauty – Haircare, Bath & Shower': [1000, 0, 9999999, 8],
  'Beauty – Makeup': [1000, 0, 9999999, 7],
  'Luxury Beauty': [500, 0, 1000, 9, 9999999, 10],
  'Face Wash': [500, 0, 1000, 9, 9999999, 10],
  'Moisturizer Cream': [500, 0, 1000, 9, 9999999, 9.5],
  'Sunscreen': [500, 0, 1000, 9, 9999999, 9.5],
  'Deodorants': [500, 0, 1000, 6.5, 9999999, 7],
  'Facial Steamers': [500, 0, 9999999, 7],
  'Beauty – Other Products': [500, 0, 9999999, 9],
  'Personal Care – Grooming & Styling': [1000, 0, 9999999, 9.5],
  'Personal Care – Electric Massagers': [1000, 0, 9999999, 14.5],
  'Personal Care – Glucometer & Strips': [1000, 0, 9999999, 5.5],
  'Personal Care – Thermometers': [1000, 0, 9999999, 10.5],
  'Personal Care – Blood Pressure Monitors': [1000, 0, 9999999, 7.5],
  'Personal Care – Electric Pain Relief Devices': [1000, 0, 9999999, 7.5],
  'Personal Care – Electric Toothbrush, Oral Irrigators': [1000, 0, 9999999, 7.5],
  'Personal Care – Weighing Scales': [1000, 0, 9999999, 13.5],
  'Personal Care – Other Products': [1000, 0, 9999999, 7.5],
  'Health – Sports Nutrition & Meal Replacement': [300, 0, 500, 9, 9999999, 9.5],
  'Health – Medical Equipment, Sexual Wellness, Elderly Care': [1000, 0, 9999999, 2],
  'Health – Regulated Healthcare': [1000, 0, 9999999, 2],
  'Health – Ayurvedic, Homeopathic & Alternate Medicine': [1000, 0, 9999999, 7],
  'Health – Household Cleaning, Laundry, Air Fresheners': [1000, 0, 9999999, 8],
  'Feminine Hygiene & Care': [1000, 0, 9999999, 8],
  'Vitamins & Mineral Health Supplements': [300, 0, 500, 9, 1000, 10.5, 9999999, 11],
  'Body Support – Wearables & Soft Aids': [1000, 0, 9999999, 2],
  'OTC Medicine': [500, 12, 9999999, 15],
  'Pharmacy – Prescription Medicines': [9999999, 8],
  'Health – Other Products': [300, 0, 9999999, 11.5],
  'Bean Bags & Inflatables': [1000, 0, 9999999, 8],
  'Rugs & Doormats': [1000, 0, 9999999, 9],
  'Mattresses': [500, 25.5, 1000, 0, 20000, 16, 9999999, 13.5],
  'Clocks': [1000, 0, 9999999, 10],
  'Wall Art': [1000, 0, 9999999, 13.5],
  'Home Fragrance & Candles': [1000, 0, 9999999, 12.5],
  'Bedsheets, Blankets & Covers': [1000, 0, 9999999, 8.5],
  'Home Furnishing (excl. Curtains)': [1000, 0, 9999999, 11],
  'Home Improvement – Accessories': [1000, 0, 9999999, 13.5],
  'Office Furniture – Study Table, Office/Gaming Chairs': [500, 16.5, 1000, 0, 15000, 15.5, 9999999, 11],
  'Tiles & Flooring Accessories': [300, 0, 9999999, 8],
  'Wires (Electrical)': [300, 0, 9999999, 10],
  'Home Storage (excl. Kitchen)': [1000, 0, 9999999, 15],
  'Home Improvement – Wallpapers': [1000, 0, 9999999, 6.5],
  'Home Decor Products': [1000, 0, 9999999, 17],
  'Wall Paints & Tools': [1000, 0, 9999999, 6],
  'Home – Waste & Recycling': [1000, 0, 9999999, 8],
  'Craft Materials': [1000, 0, 9999999, 5],
  'Water Purifier & Accessories': [1000, 0, 5000, 6.5, 9999999, 7.5],
  'Water Heaters & Accessories': [1000, 0, 5000, 8, 9999999, 9],
  'Home Improvement – Kitchen & Bath, Hardware': [1000, 0, 9999999, 12],
  'Sanitaryware': [1000, 0, 10000, 8, 9999999, 6],
  'Home Safety & Security Systems': [1000, 0, 9999999, 6],
  'Inverter & Batteries': [1000, 0, 9999999, 4.5],
  'Cleaning & Home Appliances': [1000, 0, 5000, 7.5, 9999999, 8.5],
  'Ladders': [1000, 0, 9999999, 8],
  'Indoor Lighting – Wall, Ceiling, Smart Lighting': [1000, 0, 9999999, 12.5],
  'Doors & Windows': [1000, 0, 5000, 6, 9999999, 2],
  'LED Bulbs & Battens': [1000, 0, 9999999, 14],
  'Cushion Covers': [1000, 0, 9999999, 10],
  'Curtains & Curtain Accessories': [1000, 0, 9999999, 16.5],
  'Slipcovers & Kitchen Linens': [1000, 0, 9999999, 15.5],
  'Safes & Lockers': [1000, 0, 9999999, 12],
  'Lawn & Garden – Solar Panels': [1000, 0, 9999999, 2],
  'Lawn & Garden – Leaf Blower & Water Pump': [1000, 0, 9999999, 6.5],
  'Lawn & Garden – Chemical Pest Control': [1000, 0, 9999999, 9],
  'Lawn & Garden – Solar Devices': [1000, 0, 9999999, 8],
  'Lawn & Garden – Plants, Seeds, Tools': [1000, 0, 9999999, 11],
  'Lawn & Garden – Outdoor Equipment, Mowers': [1000, 0, 9999999, 6.5],
  'Lawn & Garden – Other Products': [1000, 0, 15000, 10, 9999999, 5],
  'Home – Other Products': [1000, 0, 9999999, 18],
  'Home Improvement – Other Products': [1000, 0, 9999999, 13.5],
  'Indoor Lighting – Others': [1000, 0, 9999999, 16],
  'Furniture – Other Products': [1000, 0, 15000, 15.5, 9999999, 11],
  'Large Furniture – Sofa, Beds, Wardrobes': [1000, 0, 15000, 15.5, 9999999, 11],
  'Shelfs, Cabinets, Racks & Item Stands': [1000, 0, 9999999, 13],
  'Kitchen – Glassware & Ceramicware': [1000, 0, 9999999, 12.5],
  'Kitchen – Gas Stoves & Pressure Cookers': [1000, 0, 1500, 6, 9999999, 10],
  'Containers, Boxes, Bottles & Kitchen Storage': [1000, 0, 9999999, 12],
  'Cookware, Tableware & Dinnerware': [1000, 0, 9999999, 12.5],
  'Kitchen Tools – Choppers, Knives, Bakeware': [1000, 0, 9999999, 12.5],
  'Major Appliances Accessories': [1000, 0, 9999999, 16],
  'Chimneys': [9999999, 9.5],
  'Refrigerators': [9999999, 5],
  'Small Appliances': [1000, 0, 5000, 4.5, 9999999, 8],
  'Fans & Robotic Vacuums': [1000, 0, 5000, 4.5, 9999999, 8],
  'Major Appliances – Other Products': [9999999, 5.5],
  'Kitchen – Other Products': [1000, 0, 9999999, 12.5],
  'Bicycles': [1000, 0, 9999999, 6],
  'Gym Equipment': [1000, 0, 35000, 12, 9999999, 10],
  'Gym Weights': [1000, 0, 9999999, 5.5],
  'Sports – Footwear': [1000, 0, 9999999, 16],
  'Sports – Cricket, Badminton, Tennis, Swimming': [1000, 0, 9999999, 8.5],
  'Sports – Cricket Bats, Badminton Racquets, TT Tables': [1000, 0, 9999999, 7.5],
  'Sports Collectibles': [300, 0, 9999999, 17],
  'Sports – Other Products': [1000, 0, 9999999, 13],
  'Netting Cover': [1000, 0, 9999999, 5],
  'Coin Collectibles': [300, 0, 9999999, 15],
  'Consumable Physical Gift Card': [9999999, 5],
  'E-mail Gift Card': [9999999, 0],
  'Warranty Services': [300, 10, 500, 29, 9999999, 30],
};

// Get all category names sorted
const CATEGORIES = Object.keys(REFERRAL_FEE_SCHEDULE).sort();

// Pre-compute category table rows to avoid hydration issues
const CATEGORY_TABLE_ROWS = CATEGORIES.slice(0, 50).map((cat, idx) => {
  const tiers = REFERRAL_FEE_SCHEDULE[cat];
  const tierStr = [];
  for (let i = 0; i < tiers.length; i += 2) {
    tierStr.push(`≤₹${tiers[i].toLocaleString()}: ${tiers[i+1]}%`);
  }
  return { cat, tierStr: tierStr.join(' → '), isEven: idx % 2 === 0 };
});

// Pre-build category options for Select component
const CATEGORY_OPTIONS = CATEGORIES.map(cat => ({ value: cat, label: cat }));

// ============================================================
// CALCULATION FUNCTIONS - Exactly matching Excel logic
// ============================================================

// Calculate Referral Fee based on category and price tiers
const calculateReferralFee = (category, price) => {
  const tiers = REFERRAL_FEE_SCHEDULE[category];
  if (!tiers) return { rate: 0, fee: 0 };
  
  let rate = 0;
  
  // Process tiers in pairs (max, rate%)
  for (let i = 0; i < tiers.length; i += 2) {
    const tierMax = tiers[i];
    const tierRate = tiers[i + 1];
    
    if (price <= tierMax) {
      rate = tierRate;
      break;
    }
  }
  
  const fee = (price * rate) / 100;
  return { rate, fee };
};

// Calculate Closing Fee based on mode and price slab
const calculateClosingFee = (mode, price) => {
  const fees = CLOSING_FEES[mode];
  if (!fees) return 0;
  
  if (price <= 300) return fees[0];
  if (price <= 500) return fees[300];
  if (price <= 1000) return fees[500];
  return fees[1000];
};

// Calculate Weight Handling Fee based on mode, step level, zone (for FBA), and weight
const calculateWeightHandlingFee = (mode, stepLevel, zone, weight) => {
  let fees;
  
  if (mode === 'FBA') {
    fees = FBA_WEIGHT_FEES[stepLevel]?.[zone];
  } else if (mode === 'Seller Flex') {
    fees = SELLER_FLEX_WEIGHT_FEES[stepLevel];
  } else if (mode === 'Easy Ship') {
    fees = EASY_SHIP_WEIGHT_FEES[stepLevel];
  } else {
    return 0; // Self Ship has no weight handling (user provides courier cost)
  }
  
  if (!fees) return 0;
  
  // Weight slab logic from Excel
  if (weight <= 0.5) {
    return fees.base_0_5;
  } else if (weight <= 1) {
    return fees.base_0_5_1;
  } else if (weight <= 2) {
    return fees.base_1_2;
  } else if (weight <= 5) {
    // Base for 1-2kg + additional per kg for 2-5kg range
    const additionalKg = weight - 2;
    return fees.base_1_2 + (Math.ceil(additionalKg) * fees.per_kg_2_5);
  } else {
    // Base for 1-2kg + full 2-5kg charge + additional per kg above 5kg
    const kg_2_to_5 = 3; // 5 - 2 = 3 kg
    const additionalKgAbove5 = weight - 5;
    return fees.base_1_2 + (kg_2_to_5 * fees.per_kg_2_5) + (Math.ceil(additionalKgAbove5) * fees.per_kg_above_5);
  }
};

// Calculate all fees for a fulfillment mode
const calculateModeFees = (mode, inputs) => {
  const { category, sellingPrice, stepLevel, zone, weight, productCost, courierCost } = inputs;
  
  // Referral Fee
  const referral = calculateReferralFee(category, sellingPrice);
  
  // Closing Fee
  const closingFee = calculateClosingFee(mode, sellingPrice);
  
  // Weight Handling
  const weightHandlingFee = calculateWeightHandlingFee(mode, stepLevel, zone, weight);
  
  // Pick & Pack (FBA only)
  const pickPackFee = mode === 'FBA' ? FBA_PICK_PACK_FEE : 0;
  
  // Tech Fee (Seller Flex only)
  const techFee = mode === 'Seller Flex' ? SELLER_FLEX_TECH_FEE : 0;
  
  // For Self Ship, use courier cost instead of weight handling
  const shippingCost = mode === 'Self Ship' ? (courierCost || 0) : weightHandlingFee;
  
  // Total Fees (pre-GST)
  const totalFeesPreGST = referral.fee + closingFee + shippingCost + pickPackFee + techFee;
  
  // GST on fees
  const gstOnFees = totalFeesPreGST * GST_RATE;
  
  // Total Fees (incl GST)
  const totalFeesInclGST = totalFeesPreGST + gstOnFees;
  
  // Bank Settlement = Selling Price - Total Fees (incl GST)
  const bankSettlement = sellingPrice - totalFeesInclGST;
  
  // Profit = Bank Settlement - Product Cost
  const profit = bankSettlement - productCost;
  
  // Profit Margin %
  const profitMargin = sellingPrice > 0 ? (profit / sellingPrice) * 100 : 0;
  
  return {
    mode,
    referralRate: referral.rate,
    referralFee: referral.fee,
    closingFee,
    weightHandlingFee: shippingCost,
    pickPackFee,
    techFee,
    totalFeesPreGST,
    gstOnFees,
    totalFeesInclGST,
    bankSettlement,
    profit,
    profitMargin,
  };
};

// ============================================================
// REACT COMPONENT
// ============================================================

const AmazonRevenueCalculator = () => {
  // Input states
  const [category, setCategory] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [stepLevel, setStepLevel] = useState('Standard');
  const [zone, setZone] = useState('National');
  const [weight, setWeight] = useState('');
  const [productCost, setProductCost] = useState('');
  const [courierCost, setCourierCost] = useState('');
  
  // UI states
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [errors, setErrors] = useState({});

  // Validation
  const validateInputs = useCallback(() => {
    const newErrors = {};
    
    if (!category) newErrors.category = 'Please select a category';
    if (!sellingPrice || parseFloat(sellingPrice) <= 0) newErrors.sellingPrice = 'Selling price must be > 0';
    if (!weight || parseFloat(weight) <= 0) newErrors.weight = 'Weight must be a positive number';
    if (!stepLevel) newErrors.stepLevel = 'Please select a step level';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [category, sellingPrice, weight, stepLevel]);

  // Calculate results for all modes
  const results = useMemo(() => {
    if (!category || !sellingPrice || !weight) return null;
    
    const inputs = {
      category,
      sellingPrice: parseFloat(sellingPrice) || 0,
      stepLevel,
      zone,
      weight: parseFloat(weight) || 0,
      productCost: parseFloat(productCost) || 0,
      courierCost: parseFloat(courierCost) || 0,
    };
    
    if (inputs.sellingPrice <= 0 || inputs.weight <= 0) return null;
    
    const modes = ['Easy Ship', 'FBA', 'Seller Flex', 'Self Ship'];
    const calculations = modes.map(mode => calculateModeFees(mode, inputs));
    
    // Find best option (highest profit)
    const bestMode = calculations.reduce((best, current) => 
      current.profit > best.profit ? current : best
    );
    
    return {
      calculations,
      bestMode: bestMode.mode,
      bestProfit: bestMode.profit,
    };
  }, [category, sellingPrice, stepLevel, zone, weight, productCost, courierCost]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '₹0';
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0%';
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="min-h-screen pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>Free E-commerce Tools for Sellers | Obiquiste Solutions</title>
        <meta name="description" content="Free tools for Amazon & Flipkart sellers. Inventory reconciliation, sales calculator & more. Boost your e-commerce business with our free seller tools." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/tools/amazon-revenue-calculator" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/tools" className="text-slate-700 hover:text-primary font-medium">Tools</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Amazon Revenue Calculator</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Calculator className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-3">
            Amazon Revenue <span className="text-primary">Calculator</span>
          </h1>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            Compare fees across all Amazon fulfillment modes instantly. Enter your product details once and see Easy Ship, FBA, Seller Flex, and Self Ship compared side-by-side.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Rates effective March 16, 2026 • All fees exclusive of 18% GST
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sticky top-32">
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center">
                <Package className="w-5 h-5 mr-2 text-primary" />
                Product Details
              </h2>
              
              <div className="space-y-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category *
                  </label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger 
                      className={`w-full ${errors.category ? 'border-red-400' : 'border-slate-300'}`}
                      data-testid="category-select"
                    >
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {CATEGORY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
                </div>

                {/* Selling Price */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Selling Price (₹) *
                  </label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      value={sellingPrice}
                      onChange={(e) => setSellingPrice(e.target.value)}
                      placeholder="e.g. 599"
                      className={`pl-9 ${errors.sellingPrice ? 'border-red-400' : ''}`}
                      data-testid="selling-price-input"
                    />
                  </div>
                  {errors.sellingPrice && <p className="text-red-500 text-xs mt-1">{errors.sellingPrice}</p>}
                </div>

                {/* Step Level */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Step Level *
                  </label>
                  <select
                    value={stepLevel}
                    onChange={(e) => setStepLevel(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    data-testid="step-level-select"
                  >
                    <option value="Premium">Premium / Advanced</option>
                    <option value="Standard">Standard</option>
                    <option value="Basic">Basic</option>
                  </select>
                </div>

                {/* Zone (FBA only) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Zone (FBA only)
                  </label>
                  <select
                    value={zone}
                    onChange={(e) => setZone(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    data-testid="zone-select"
                  >
                    <option value="National">National</option>
                    <option value="Regional">Regional</option>
                  </select>
                </div>

                {/* Weight */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Weight (kg) *
                  </label>
                  <Input
                    type="number"
                    step="0.1"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="e.g. 0.5"
                    className={errors.weight ? 'border-red-400' : ''}
                    data-testid="weight-input"
                  />
                  {errors.weight && <p className="text-red-500 text-xs mt-1">{errors.weight}</p>}
                </div>

                {/* Product Cost */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Product Cost (₹)
                  </label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      value={productCost}
                      onChange={(e) => setProductCost(e.target.value)}
                      placeholder="e.g. 199"
                      className="pl-9"
                      data-testid="product-cost-input"
                    />
                  </div>
                </div>

                {/* Self Ship Courier Cost */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Self Ship Courier Cost (₹)
                  </label>
                  <div className="relative">
                    <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      value={courierCost}
                      onChange={(e) => setCourierCost(e.target.value)}
                      placeholder="e.g. 50"
                      className="pl-9"
                      data-testid="courier-cost-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Required for Self Ship comparison</p>
                </div>
              </div>

              {/* Toggle Breakdown */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowBreakdown(!showBreakdown)}
                  className="flex items-center text-sm text-slate-600 hover:text-primary transition-colors"
                >
                  {showBreakdown ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {showBreakdown ? 'Hide' : 'Show'} Fee Breakdown
                </button>
              </div>
            </div>
          </motion.div>

          {/* Results Panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            {!results ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Enter Product Details</h3>
                <p className="text-slate-500 text-sm">
                  Fill in the category, selling price, and weight to see fee calculations across all fulfillment modes.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Best Option Banner */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 text-white flex items-center justify-between">
                  <div className="flex items-center">
                    <CheckCircle2 className="w-8 h-8 mr-3" />
                    <div>
                      <p className="text-sm text-green-100">Best Option (Highest Profit)</p>
                      <p className="text-xl font-bold">{results.bestMode}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-green-100">Profit</p>
                    <p className="text-2xl font-bold">{formatCurrency(results.bestProfit)}</p>
                  </div>
                </div>

                {/* Comparison Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.calculations.map((calc) => (
                    <div
                      key={calc.mode}
                      className={`bg-white rounded-xl border-2 p-5 transition-all ${
                        calc.mode === results.bestMode 
                          ? 'border-green-500 shadow-lg shadow-green-100' 
                          : 'border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-lg font-bold text-slate-900">
                          {calc.mode}
                        </h3>
                        {calc.mode === results.bestMode && (
                          <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                            BEST
                          </span>
                        )}
                      </div>

                      {/* Fee Breakdown */}
                      {showBreakdown && (
                        <div className="space-y-2 mb-4 text-sm">
                          <div className="flex justify-between text-slate-600">
                            <span>Referral Fee ({formatPercent(calc.referralRate)})</span>
                            <span>{formatCurrency(calc.referralFee)}</span>
                          </div>
                          <div className="flex justify-between text-slate-600">
                            <span>Closing Fee</span>
                            <span>{formatCurrency(calc.closingFee)}</span>
                          </div>
                          <div className="flex justify-between text-slate-600">
                            <span>{calc.mode === 'Self Ship' ? 'Courier Cost' : 'Weight Handling'}</span>
                            <span>{formatCurrency(calc.weightHandlingFee)}</span>
                          </div>
                          {calc.pickPackFee > 0 && (
                            <div className="flex justify-between text-slate-600">
                              <span>Pick & Pack</span>
                              <span>{formatCurrency(calc.pickPackFee)}</span>
                            </div>
                          )}
                          {calc.techFee > 0 && (
                            <div className="flex justify-between text-slate-600">
                              <span>Technology Fee</span>
                              <span>{formatCurrency(calc.techFee)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-slate-700 font-medium pt-2 border-t border-slate-100">
                            <span>Total Fees (pre-GST)</span>
                            <span>{formatCurrency(calc.totalFeesPreGST)}</span>
                          </div>
                          <div className="flex justify-between text-slate-600">
                            <span>GST 18%</span>
                            <span>{formatCurrency(calc.gstOnFees)}</span>
                          </div>
                          <div className="flex justify-between text-slate-800 font-semibold">
                            <span>Total Fees (incl GST)</span>
                            <span className="text-red-600">{formatCurrency(calc.totalFeesInclGST)}</span>
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      <div className={`space-y-2 ${showBreakdown ? 'pt-3 border-t border-slate-200' : ''}`}>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Bank Settlement</span>
                          <span className="font-semibold text-slate-800">{formatCurrency(calc.bankSettlement)}</span>
                        </div>
                        <div className="flex justify-between items-center bg-slate-50 rounded-lg p-3 -mx-1">
                          <span className="font-semibold text-slate-700">Profit</span>
                          <div className="text-right">
                            <span className={`text-xl font-bold ${calc.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(calc.profit)}
                            </span>
                            <span className={`block text-xs ${calc.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPercent(calc.profitMargin)} margin
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Info Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How to read this comparison:</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                      <li><strong>Bank Settlement</strong> = Selling Price − Total Fees</li>
                      <li><strong>Profit</strong> = Bank Settlement − Product Cost</li>
                      <li>FBA includes Pick & Pack fee; Seller Flex includes Technology fee</li>
                      <li>Self Ship requires your courier cost for accurate comparison</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Category Reference */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12"
        >
          <details className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <summary className="px-6 py-4 cursor-pointer font-heading font-bold text-slate-800 flex items-center justify-between hover:bg-slate-50">
              <span className="flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-primary" />
                Referral Fee Reference ({CATEGORIES.length} Categories)
              </span>
              <ChevronRight className="w-5 h-5 text-slate-400 transition-transform" />
            </summary>
            <div className="px-6 pb-6 max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 font-semibold text-slate-700">Category</th>
                    <th className="text-center py-2 font-semibold text-slate-700">Fee Tiers</th>
                  </tr>
                </thead>
                <tbody>
                  {CATEGORY_TABLE_ROWS.map((row) => (
                    <tr key={row.cat} className={row.isEven ? 'bg-slate-50' : ''}>
                      <td className="py-2 text-slate-700">{row.cat}</td>
                      <td className="py-2 text-center text-slate-600 text-xs">{row.tierStr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {CATEGORIES.length > 50 && (
                <p className="text-center text-slate-500 text-sm mt-4">
                  Showing 50 of {CATEGORIES.length} categories. Select from dropdown to see rates.
                </p>
              )}
            </div>
          </details>
        </motion.div>
      </div>
    </div>
  );
};

export default AmazonRevenueCalculator;
